import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { FormArray, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertService, AuthenticationService } from '@app/_services';
import * as moment from 'moment';
import { PrimeNGConfig } from 'primeng/api';
import { Quote, QuoteService } from '..';
import { ReservationService } from '@app/_components/reservation';
import { Calendar } from 'primeng/calendar';
import { GuestMasterProfileListComponent } from '@app/_components/guest-master-profile/guest-master-profile-list/guest-master-profile-list.component';
import { User } from '@app/_models';
import { Subscription } from 'rxjs';
import {CurrencyPipe} from "@angular/common";
import {GuestMasterProfileComponent} from "@app/_components/guest-master-profile/guest-master-profile/guest-master-profile.component";
import {GuestMasterProfileService} from "@app/_components/guest-master-profile";
import { ActivatedRoute } from '@angular/router';
import { DripCampaignService } from '@app/_components/drip/drip-campaign.service';
import { DripCampaignInstance } from '@app/_models/drip-campaign';

@Component({
    selector: 'app-quote',
    templateUrl: './quote.component.html',
    styleUrls: ['./quote.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class QuoteComponent implements OnInit, OnDestroy {

  @ViewChild('checkInDateCal') public checkInDateCalender: Calendar;
  @ViewChild('checkOutDateCal') public checkOutDateCalender: Calendar;
  @ViewChild('expireDateCal') public expireDateCalender: Calendar;

  constructor(
    private primengConfig: PrimeNGConfig,
    private quoteService: QuoteService,
    public reservationService: ReservationService,
    private guestMasterProfileService: GuestMasterProfileService,
    private alertService: AlertService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authenticationService: AuthenticationService,
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private currencyPipe: CurrencyPipe,
    private dripCampaignService: DripCampaignService,
    // Optional because QuoteComponent may also render outside a MatDialog
    // (the component is reused on the reservation flow). Null there.
    @Optional() private dialogRef: MatDialogRef<QuoteComponent>,
    @Inject(MAT_DIALOG_DATA) data: any) {
    this.formTitle = data.title,
      this.quote = data.quote,
      this.msgOrigin = data?.msgOrigin;
      this.msgRequest = data?.msgRequest;
      // Quote source (which entry point opened this dialog) for the Quote
      // Analytics dashboard — see QuoteSource enum.  Caller passes one of
      // BEST_FIT / INBOX_BEST_FIT / QUOTE_LIST / TAPE_CHART / INBOX_REPLY.
      // Null falls through to backend OTHER bucket.
      this.source = data?.source ?? null;
      this.selectedProperties = data.quote?.selectedProperties;
      // Multi-window quote input (v2.6): when the tape chart flex flow flattens
      // multiple date ranges into one quote it passes `selectedWindows`, each
      // entry carrying its own { propertyCode, shortTermRentalId, startDate,
      // endDate, numNights, property }. When present we iterate this instead
      // of `selectedProperties` in addQuoteDetails(), push per-row date
      // controls into the form, and null out the parent checkIn/checkOut in
      // sendQuote() so the backend stamps the envelope span [min, max]
      // across all rows. Null for legacy single-window quotes.
      this.selectedWindows = data.quote?.selectedWindows ?? null;
      // Smart-parsed context carried forward from the tape chart / inbox.
      // Holds guest name/email/phone, adult/child counts, and (when it was
      // a flex request) a marker so downstream UI can flag it.
      this.quoteContext = data?.quoteContext ?? null;
      this.incomingGuestInfo = data?.quote?.guestInfo ?? null;
      this.incomingNumAdults   = data?.quote?.numAdults;
      this.incomingNumChildren = data?.quote?.numChildren;
    this.childrens = this.reservationService.children;
    this.quoteStatuses = this.reservationService.quoteStatuses;
    this.numAdults = this.reservationService.numAdults;
    this.quoteDiscPcts = this.quoteService.quoteDiscPct;
    this.currentUserSubscription = this.authenticationService.currentUser.subscribe(user => {
      this.currentUser = user;
    });
  }

  @Input() quote: Quote;
  @Output() doAdd = new EventEmitter<any>();
  @Output() doUpdate = new EventEmitter<any>();
  @Output() doDelete = new EventEmitter<any>();
  @Output() doEmailEvent = new EventEmitter<any>();

  // data
  quoteId: number;
  quoteRequest = {};
  wikiQuote: any;
  showShortInvoiceDetail = true;
  showQuoteWindow = false;
  errorList: any = [];
  formError: any;
  quotePropertyCode: any;
  position = 'Top';
  couponCode: any;
	isSearching = false;
	checkCouponValidOrNot = false;
  invalidCouponCode: boolean;
	couponCodeSuccessMsg: boolean;
	isCouponCodeIsApply: boolean;
	showCoupon: boolean;

  // Drip campaign status panel
  dripInstance: DripCampaignInstance | null = null;
  dripPanelCollapsed = false;
  dripLoading = false;

  // forms
  form1: UntypedFormGroup;
  formLoading = false;
  formAlert: String;
  formTitle: String;

  /**
   * True while addQuoteDetails() is fetching per-property thumbnail + rate
   * details in parallel. Until the first Promise.all batch resolves the
   * property list below is empty — without this flag the reservationist
   * saw a blank area and couldn't tell whether the backend was slow or
   * there were simply no matching properties.
   */
  loadingQuoteDetails = false;

  /**
   * True while openQuoteWindow() → getQuoteForSearchRates() is fetching
   * the authoritative quote breakdown for a single property. The inner
   * p-dialog's "Property: X" header renders instantly, so without this
   * flag the invoice area is blank during the round-trip. Toggles back
   * off as soon as the response (or error) lands.
   */
  loadingWikiQuote = false;

  childrens: any[];
  quoteStatuses: any;
  numAdults: any[];
  quoteDiscPcts: any[];
  maxDate: Date;
  minDate: Date;
  selectedProperties: any = [];
  /**
   * Per-row windows for multi-window quotes (v2.6). Present when the tape
   * chart's flex flow built this quote from several date ranges. Each entry:
   *   { propertyCode, shortTermRentalId, startDate: Date, endDate: Date,
   *     numNights: number, property: <full property payload> }
   * Null for legacy single-window quotes — code that needs to branch
   * should use the `isMultiWindow` getter rather than truthy-checking this
   * field directly so the intent is obvious at call sites.
   */
  selectedWindows: any[] | null = null;
  updatedCheckInDate: Date;
  checkOutDateBasedOnCheckinDate: Date;
  isDisplayClearDates = false;
  isDateLessThanCheckinDate = false;
  currentUser: User;
  currentUserSubscription: Subscription;
  reservedProperties: any[];
  receivedGuestMasterProfileId=null;
  msgOrigin: any;
  msgRequest: any;
  /** Entry point that opened this dialog — see QuoteSource enum. Mapped to
   *  str_quote.source on the backend so the analytics dashboard can show
   *  which tool the team uses most. */
  source: number | null = null;

  /**
   * True when we hydrated the guest form from parsed email but could NOT
   * find an existing Guest Master Profile with that email. The backend
   * requires a guestMasterProfileId on every /quotes/create call, so the
   * rep must either (a) create a new GMP via createNewGuestFromPrefill()
   * or (b) find a different existing GMP via findGuest() before we allow
   * sendQuote() to POST. Template uses this to render a warning banner
   * with a one-click "Create Profile" button.
   */
  needsNewGmp = false;

  /**
   * Parsed guest fields we pre-populated the form with; carried separately
   * so that when the rep clicks "Create Profile" we can hand a clean
   * GuestMasterProfile-shaped object to GuestMasterProfileComponent
   * without whatever the rep may have hand-edited meanwhile (we still
   * respect their edits via the form, but this is the original seed).
   */
  pendingNewGuestPrefill: { firstName?: string; lastName?: string; emailAddress?: string; mobilePhone?: string } | null = null;

  /**
   * Full smart-parsed context from the inbox — we carry it so the
   * quote-confirmation email / thread can surface "this quote answers
   * <guest>'s request for 5 nights in August". Not persisted on the
   * QuoteVO directly; used only client-side.
   */
  quoteContext: any = null;
  /** Flattened guest info { guestName, guestEmail, guestPhone } when provided by the tape chart. */
  incomingGuestInfo: { guestName?: string; guestEmail?: string; guestPhone?: string } | null = null;
  incomingNumAdults: number | undefined;
  incomingNumChildren: number | undefined;
  /** Shown as a small chip at the top of the quote dialog when context is present. */
  showContextChip = false;

  /**
   * True iff this dialog was opened with a flattened `selectedWindows`
   * array (v2.6 multi-window quote). Template uses this to show the
   * per-row "Window" column, and sendQuote() uses it to null the parent
   * checkIn/checkOut so the backend stamps the envelope span.
   */
  get isMultiWindow(): boolean {
    return Array.isArray(this.selectedWindows) && this.selectedWindows.length > 0;
  }

  validationMessages = {
    shortTermRentalId: [{ type: 'required', message: 'Required' }],
    emailAddress: [{ type: 'required', message: 'Required' },
      { type: 'email', message: 'Not a valid Email' }],
    mobilePhone: [],
    numAdults: [{ type: 'required', message: 'Required' }],
    numChildren: [{ type: 'required', message: 'Required' }],
    checkOutDateTime: [{ type: 'required', message: 'Required' }],
    guestNotes: [],
    sendEmail: [{ type: 'required', message: 'Please select Email or Text' }],
    sendText: [{ type: 'required', message: 'Required' }],
    quoteExpirationDateTime: [{ type: 'required', message: 'Required' }],
    checkInDateTime: [{ type: 'required', message: 'Required' }],
    firstName: [{ type: 'required', message: 'Required' }],
    lastName: [{ type: 'required', message: 'Required' }],
    numQuoteStatus: [{ type: 'required', message: 'Required' }],
    quotePrice: [],
    quoteDiscPct: [],
    quoteStatus:[],
  };

  ngOnInit() {
    this.primengConfig.ripple = true;
    let today = new Date();
    let month = today.getMonth();
    let year = today.getFullYear();
    let day = today.getDate();
    this.updatedCheckInDate = new Date(year, month, day);
    this.minDate = new Date(year, month, day);
    this.maxDate = new Date(year + 3, month, day);
    this.formBuild();

    // Always stamp the currently logged-in user as the reservationist.
    // Must be set here (not only inside quoteDataLoad) so new quotes opened
    // from BestFit (where this.quote is null and quoteDataLoad is skipped)
    // also carry the correct reservationistUserId to the backend.
    // quoteDataLoad() will overwrite this with the same value for edit mode,
    // so there is no conflict.
    if (this.currentUser?.userId) {
      this.form1.patchValue({ reservationistUserId: this.currentUser.userId });
    }

    // Multi-window quote (v2.6): parent checkIn/checkOut are meaningless
    // here — each detail row has its own window. Strip the "required"
    // validators so the form can submit with null parent dates. The
    // template already hides / dims the top-level date pickers in this
    // mode. Backend stamps the envelope span [min, max] over the rows.
    if (this.isMultiWindow) {
      this.form1.get('checkInDateTime')?.clearValidators();
      this.form1.get('checkInDateTime')?.updateValueAndValidity({ emitEvent: false });
      this.form1.get('checkOutDateTime')?.clearValidators();
      this.form1.get('checkOutDateTime')?.updateValueAndValidity({ emitEvent: false });
    }

    // if we were passed the whole quote object
    if (this.quote) {
      this.quoteDataLoad();
    } else {
      this.activatedRoute.params.subscribe(params => {
        this.quoteId = params.quoteId; // (+) converts string 'id' to a number
      });
      if (this.quoteId != null && this.quoteId != undefined) {
        this.findById();
      }
    }

    // Pre-fill from smart-parsed context (inbox -> tape chart -> here).
    // Must run AFTER quoteDataLoad() so we don't clobber existing data
    // when editing a saved quote.
    this.applyIncomingContext();
  }

  /**
   * Fills form fields using the context supplied by the caller:
   *   - numAdults / numChildren  (from parsed email or tape chart choice)
   *   - guest email / first name / last name / phone
   *
   * Guest identity is hydrated GMP-first (Guest Master Profile is the
   * source of truth):
   *   1. If we already have a guestMasterProfileId (editing an existing
   *      quote, or the inbox/tape-chart chain forwarded one), fetch the
   *      canonical GMP record by id and patch from it.
   *   2. Else if we have a parsed guest email, search GMPs by that
   *      email — if exactly one match (or first match) exists, use it
   *      as the authoritative source and record its id so subsequent
   *      Edit Guest calls reuse the same profile.
   *   3. Only fall back to raw parsed-email fields when no GMP exists
   *      (new guest — reservationist will save a new GMP later).
   *
   * Only sets values that are still empty so we never overwrite what
   * the user has already typed.
   */
  applyIncomingContext(): void {
    if (this.incomingNumAdults != null && !this.form1.value.numAdults) {
      this.form1.patchValue({ numAdults: this.incomingNumAdults });
    }
    if (this.incomingNumChildren != null && this.form1.value.numChildren == null) {
      this.form1.patchValue({ numChildren: this.incomingNumChildren });
    }
    // Always prefer the parsed values over the component defaults (2/0)
    if (this.incomingNumAdults != null && this.incomingNumAdults !== 2) {
      this.form1.patchValue({ numAdults: this.incomingNumAdults });
    }
    if (this.incomingNumChildren != null && this.incomingNumChildren !== 0) {
      this.form1.patchValue({ numChildren: this.incomingNumChildren });
    }

    this.showContextChip = !!(this.quoteContext || this.incomingGuestInfo);

    // Path 1: known GMP id → authoritative lookup by id
    const knownGmpId = this.quote?.guestMasterProfileId || this.receivedGuestMasterProfileId;
    if (knownGmpId) {
      this.hydrateGuestFromMasterProfileId(knownGmpId);
      return;
    }

    // Path 2: parsed email → try to find an existing GMP by email
    const gi = this.incomingGuestInfo;
    if (gi?.guestEmail) {
      this.guestMasterProfileService.findByEmail(gi.guestEmail).subscribe({
        next: (res: any) => {
          // Backend returns a paged envelope OR a plain list depending
          // on the endpoint shape; handle both. We also accept the
          // wrapped `{ data: { content: [...] } }` pattern used elsewhere.
          const match = this.pickFirstGuestMasterProfile(res);
          if (match && match.guestMasterProfileId) {
            this.receivedGuestMasterProfileId = match.guestMasterProfileId;
            // Broadcast to any sibling dialogs sharing this context.
            if (this.quoteContext) { this.quoteContext.resolvedGmpId = match.guestMasterProfileId; }
            this.patchGuestFromProfile(match);
          } else {
            // Before showing the "create profile" warning, check whether another
            // Quote dialog that opened in the same session has already resolved
            // (or created) a GMP for this guest. All dialogs share the same
            // quoteContext object reference, so dialog 1's write is visible here.
            if (this.quoteContext?.resolvedGmpId) {
              this.hydrateGuestFromMasterProfileId(this.quoteContext.resolvedGmpId);
              return;
            }
            // No existing GMP — fall back to raw parsed email data AND
            // nudge the rep to confirm-create a new GMP before save
            // (backend requires a guestMasterProfileId, see QuoteBL.createQuote).
            this.patchGuestFromParsedEmail(gi);
            this.markNeedsNewGmp(gi);
          }
        },
        error: (err: any) => {
          console.error('findByEmail failed — falling back to parsed email:', err);
          this.patchGuestFromParsedEmail(gi);
          // Lookup failed — if another dialog already resolved a GMP, reuse it.
          if (this.quoteContext?.resolvedGmpId) {
            this.hydrateGuestFromMasterProfileId(this.quoteContext.resolvedGmpId);
            return;
          }
          // Still no confirmed GMP — flag the nudge.
          this.markNeedsNewGmp(gi);
        }
      });
      return;
    }

    // Path 3: no GMP id AND no email — nothing to hydrate.
  }

  /** Fetch canonical GMP by id and patch the guest form fields from it. */
  private hydrateGuestFromMasterProfileId(id: number): void {
    this.guestMasterProfileService.findById(id).subscribe({
      next: (res: any) => {
        const gmp = res?.data?.guestMasterProfile || res?.data || res;
        if (gmp) {
          this.receivedGuestMasterProfileId = gmp.guestMasterProfileId || id;
          this.patchGuestFromProfile(gmp);
        } else if (this.incomingGuestInfo) {
          // Shouldn't happen, but don't strand the form empty.
          this.patchGuestFromParsedEmail(this.incomingGuestInfo);
        }
      },
      error: (err: any) => {
        console.error('findById(GMP) failed — falling back to parsed email:', err);
        if (this.incomingGuestInfo) {
          this.patchGuestFromParsedEmail(this.incomingGuestInfo);
        }
      }
    });
  }

  /**
   * Unwraps the variety of envelope shapes the GMP endpoints return
   * and returns the first matching profile (or null). Backend may
   * respond with:
   *   { data: { content: [...] } }   (paged)
   *   { data: [...] }                (list)
   *   { data: { guestMasterProfile: {...} } }  (single via findById)
   */
  private pickFirstGuestMasterProfile(res: any): any {
    const d = res?.data ?? res;
    if (!d) return null;
    if (Array.isArray(d)) return d[0] || null;
    if (Array.isArray(d.content)) return d.content[0] || null;
    if (Array.isArray(d.guestMasterProfiles)) return d.guestMasterProfiles[0] || null;
    if (d.guestMasterProfile) return d.guestMasterProfile;
    if (d.guestMasterProfileId) return d; // already a single profile
    return null;
  }

  /**
   * Patch the guestProfile sub-form from a full GMP record. Also
   * sets the top-level guestMasterProfileId on the form so a later
   * create/update round-trip sends the FK. Only fills empty fields
   * so a reservationist edit is never clobbered.
   */
  private patchGuestFromProfile(gmp: any): void {
    if (!gmp) return;
    const guestProfile = this.form1.get('guestProfile');
    if (!guestProfile) return;
    const current = guestProfile.value || {};
    const patch: any = {};
    if (gmp.guestMasterProfileId) patch.guestMasterProfileId = gmp.guestMasterProfileId;
    if (!current.firstName    && gmp.firstName)    patch.firstName    = gmp.firstName;
    if (!current.lastName     && gmp.lastName)     patch.lastName     = gmp.lastName;
    if (!current.emailAddress && gmp.emailAddress) patch.emailAddress = gmp.emailAddress;
    if (!current.mobilePhone  && (gmp.mobilePhone || gmp.phone)) {
      patch.mobilePhone = gmp.mobilePhone || gmp.phone;
    }
    if (Object.keys(patch).length) guestProfile.patchValue(patch);

    if (gmp.guestMasterProfileId && !this.form1.value.guestMasterProfileId) {
      this.form1.patchValue({ guestMasterProfileId: gmp.guestMasterProfileId });
    }

    // Broadcast the confirmed GMP id to any sibling dialogs in this session.
    if (gmp.guestMasterProfileId && this.quoteContext) {
      this.quoteContext.resolvedGmpId = gmp.guestMasterProfileId;
    }

    // A confirmed GMP id means we are no longer in "new guest" limbo.
    this.clearNeedsNewGmp();
  }

  /**
   * Raise the "new guest — create profile before save" flag and stash
   * the parsed fields so createNewGuestFromPrefill() can hand them
   * straight to the Guest Master Profile form.
   */
  private markNeedsNewGmp(gi: { guestName?: string; guestEmail?: string; guestPhone?: string }): void {
    if (!gi) return;
    let firstName = '';
    let lastName  = '';
    if (gi.guestName) {
      const parts = gi.guestName.trim().split(/\s+/);
      firstName = parts[0];
      lastName  = parts.slice(1).join(' ') || '';
    }
    this.pendingNewGuestPrefill = {
      firstName,
      lastName,
      emailAddress: gi.guestEmail || '',
      mobilePhone:  gi.guestPhone || ''
    };
    this.needsNewGmp = true;
  }

  /** Hide the nudge banner once a GMP (existing or freshly-created) is linked. */
  private clearNeedsNewGmp(): void {
    this.needsNewGmp = false;
    this.pendingNewGuestPrefill = null;
  }

  /**
   * Template hook: rep clicked "Create Profile" on the new-guest banner.
   * Opens the existing GuestMasterProfileComponent in "add" mode,
   * pre-populated with whatever we parsed out of the incoming email
   * (and whatever the rep may have edited via the quote form — current
   * form values win over the stashed prefill).
   *
   * On doAdd we capture the new guestMasterProfileId, stamp it onto
   * the quote form (both the top-level FK and the guestProfile
   * sub-form), and clear the nudge flag so sendQuote() will proceed.
   */
  createNewGuestFromPrefill(): void {
    // If another dialog in this session already resolved a GMP for this guest,
    // reuse it silently rather than opening a creation sub-dialog that would
    // produce a duplicate profile.
    if (this.quoteContext?.resolvedGmpId) {
      this.hydrateGuestFromMasterProfileId(this.quoteContext.resolvedGmpId);
      return;
    }

    // Prefer live form values over the stashed prefill — the rep may
    // have corrected a typo in the email or added a phone number since
    // we opened the dialog.
    const current = this.form1.get('guestProfile')?.value || {};
    const seed = {
      firstName:    current.firstName    || this.pendingNewGuestPrefill?.firstName    || '',
      lastName:     current.lastName     || this.pendingNewGuestPrefill?.lastName     || '',
      emailAddress: current.emailAddress || this.pendingNewGuestPrefill?.emailAddress || '',
      mobilePhone:  current.mobilePhone  || this.pendingNewGuestPrefill?.mobilePhone  || ''
    };

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.data = {
      title: 'Create Guest Master Profile',
      // Note: we intentionally do NOT pass a guestMasterProfileId here —
      // GuestMasterProfileComponent treats that as "add" vs "edit".
      guestMasterProfile: seed
    };
    const dialogRef = this.dialog.open(GuestMasterProfileComponent, dialogConfig);

    dialogRef.componentInstance.doAdd.subscribe((created: any) => {
      if (created?.guestMasterProfileId) {
        this.receivedGuestMasterProfileId = created.guestMasterProfileId;
        this.form1.patchValue({
          guestMasterProfileId: created.guestMasterProfileId,
          guestProfile: {
            guestMasterProfileId: created.guestMasterProfileId,
            firstName:    created.firstName    || seed.firstName,
            lastName:     created.lastName     || seed.lastName,
            emailAddress: created.emailAddress || seed.emailAddress,
            mobilePhone:  created.mobilePhone  || seed.mobilePhone
          }
        });
        this.clearNeedsNewGmp();
        this.messageService.add({ severity: 'success', summary: 'Guest profile created', detail: 'You can now send the quote.', life: 3000 });
      }
      dialogRef.close();
    });
  }

  /**
   * Fallback: patch the guestProfile sub-form from the raw parsed
   * email headers. Used only when we could not locate an existing
   * GMP — this guest is genuinely new and the rep will persist
   * them via Find/Edit Guest later.
   */
  private patchGuestFromParsedEmail(gi: { guestName?: string; guestEmail?: string; guestPhone?: string }): void {
    const guestProfile = this.form1.get('guestProfile');
    if (!guestProfile || !gi) return;
    const current = guestProfile.value || {};
    const patch: any = {};
    if (!current.emailAddress && gi.guestEmail) patch.emailAddress = gi.guestEmail;
    if (!current.mobilePhone  && gi.guestPhone) patch.mobilePhone  = gi.guestPhone;
    if (!current.firstName && !current.lastName && gi.guestName) {
      const parts = gi.guestName.trim().split(/\s+/);
      patch.firstName = parts[0];
      patch.lastName  = parts.slice(1).join(' ') || '';
    }
    if (Object.keys(patch).length) guestProfile.patchValue(patch);
  }

  quoteDataLoad(){
    this.quoteId = this.quote.quoteId;
      this.form1.patchValue(this.quote);
      // Default expiry = today + 4 days; use the Date directly to avoid lossy string round-trips
      let quoteExpirationDateTime = new Date();
      quoteExpirationDateTime.setDate(quoteExpirationDateTime.getDate() + 4);
      // Note: quote model has both checkInDate (date-only) and checkInDateTime (datetime).
      // The form uses checkInDateTime for the calendar control; we normalise from checkInDate here.
      this.form1.patchValue({
        checkInDateTime: new Date(this.quote.checkInDate),
        checkOutDateTime: new Date(this.quote.checkOutDate),
        quoteExpirationDateTime: quoteExpirationDateTime
      });

      this.form1.patchValue({
        reservationistUserId: this.currentUser.userId,
      });

      // Backward compat (v2.6). If this is an existing quote being loaded
      // for edit/view and ANY of its detail rows carries its own check-in
      // date, treat the whole quote as multi-window and rebuild
      // `selectedWindows` from the detail rows. addQuoteDetails() then
      // takes the multi-window branch and renders per-row date columns.
      //
      // Rationale: the DB migration was additive (nullable columns), so
      // legacy quotes have all detail rows with null per-row dates and
      // fall through here unchanged. Only quotes saved on v2.6+ ever
      // come back with populated per-row dates, and we recognise them
      // purely by data shape — no schema version column needed.
      const loadedDetails: any[] = (this.quote as any)?.quoteDetailAPIList || [];
      const hasPerRowDates = loadedDetails.some(d => d?.checkInDateTime);
      if (hasPerRowDates) {
        this.selectedWindows = loadedDetails.map(d => ({
          propertyCode:      d.propertyCode || d.shortTermRental?.propertyCode,
          shortTermRentalId: d.shortTermRentalId || d.shortTermRental?.shortTermRentalId,
          // Fall back to the parent envelope dates for rows where the
          // per-row field happens to be null (shouldn't happen for a
          // true multi-window quote, but we stay defensive).
          startDate:         d.checkInDateTime  ? new Date(d.checkInDateTime)  : new Date(this.quote.checkInDate),
          endDate:           d.checkOutDateTime ? new Date(d.checkOutDateTime) : new Date(this.quote.checkOutDate),
          numNights:         d.numNights,
          // No tape-chart `rates[]` available here — synthesise a minimal
          // property shell so buildQuoteDetailRow() can re-fetch pricing
          // via findQuoteTotalForWindow() instead of inferring from rates.
          property: {
            propertyCode:    d.propertyCode || d.shortTermRental?.propertyCode,
            shortTermRental: d.shortTermRental || { shortTermRentalId: d.shortTermRentalId, propertyCode: d.propertyCode }
          }
        }));
        // Drop the required validators — the multi-window path in
        // ngOnInit only runs when selectedWindows came in via the dialog
        // constructor, not the edit path.
        this.form1.get('checkInDateTime')?.clearValidators();
        this.form1.get('checkInDateTime')?.updateValueAndValidity({ emitEvent: false });
        this.form1.get('checkOutDateTime')?.clearValidators();
        this.form1.get('checkOutDateTime')?.updateValueAndValidity({ emitEvent: false });
      }

      this.addQuoteDetails();
      // Load drip status panel once we have a quoteId
      this.loadDripInstance();
  }

  async addQuoteDetails() {
    this.reservedProperties = [];
    // Signal the UI so it shows a "Loading properties..." row instead of
    // rendering an empty list. Promise.all below runs N parallel picture
    // + rate fetches, each several hundred ms, so without this the rep
    // stares at blank space for 1–3 seconds.
    this.loadingQuoteDetails = true;
    try {
      if (this.isMultiWindow) {
        // Multi-window path (v2.6): each entry in `selectedWindows` already
        // carries its own (property, startDate, endDate, numNights). We
        // build one form row per window — the same property can appear
        // twice with different dates, which is the whole point of the
        // feature. Rates/pricing are fetched against the window's own
        // dates, NOT the parent form dates (parent will be nulled in
        // sendQuote() so the backend stamps the envelope span).
        await Promise.all(this.selectedWindows.map(async w => {
          // If the tape-chart side couldn't resolve a full property object
          // (rare — the find() lookup returned null) synthesise a minimal
          // one from the IDs we DO have so buildQuoteDetailRow can still
          // price the row. Without this the row would silently drop.
          const property = w.property ?? {
            propertyCode: w.propertyCode,
            shortTermRental: { shortTermRentalId: w.shortTermRentalId, propertyCode: w.propertyCode }
          };
          await this.buildQuoteDetailRow(property, w.startDate, w.endDate, w.numNights);
        }));
      } else {
        await Promise.all(this.selectedProperties?.map(async property => {
          await this.buildQuoteDetailRow(property, null, null, null);
        }));
      }
    } finally {
      // Clear the spinner whether the batch succeeded, failed, or the
      // input array was empty — never leave it spinning.
      this.loadingQuoteDetails = false;
    }
  }

  /**
   * Builds one FormGroup row for the quoteDetailAPIList FormArray.
   *
   * Legacy single-window: pass (property, null, null, null) — rates and
   * totals are computed against the parent form's checkIn/checkOut.
   *
   * Multi-window (v2.6): pass (property, winStart, winEnd, numNights) —
   * rates/totals are computed against THIS window, and checkInDateTime /
   * checkOutDateTime / numNights are attached to the row so the backend
   * can persist the per-line window on str_quote_detail.
   */
  private async buildQuoteDetailRow(property: any, winStart: Date | null, winEnd: Date | null, winNumNights: number | null) {
    let yieldPrice;
    let isAvailable = true;
    // For legacy flow use the parent form dates; for multi-window use the
    // row's own window. Either way we end up with checkIn/checkOut Date
    // objects (or none, and we skip the availability scan).
    const effCheckIn = winStart ? new Date(winStart) : (this.form1.value.checkInDateTime ? new Date(this.form1.value.checkInDateTime) : null);
    const effCheckOut = winEnd ? new Date(winEnd) : (this.form1.value.checkOutDateTime ? new Date(this.form1.value.checkOutDateTime) : null);
    if (effCheckIn && effCheckOut) {
      if (property?.rates) {
        property.rates.forEach(rate => {
          let analyzedDateObj = moment(rate.analyzedDate).toDate();
          analyzedDateObj.setHours(0, 0, 0, 0);
          effCheckIn.setHours(0, 0, 0, 0);
          effCheckOut.setHours(0, 0, 0, 0);
          if (analyzedDateObj.getTime() === effCheckIn.getTime()) {
            yieldPrice = rate.yieldPriceAMT ? rate.yieldPriceAMT : rate.rateYieldPriceAMT;
          }
          if (analyzedDateObj.getTime() >= effCheckIn.getTime() && analyzedDateObj.getTime() < effCheckOut.getTime()) {
            if (rate.reservationId && rate.reservationId > 0) {
              isAvailable = false;
            }
          }
        });
      }
    }

    const shortTermRentalId = property?.rates?.[0]?.shortTermRentalId || property?.shortTermRental?.shortTermRentalId;
    if (!shortTermRentalId) {
      console.error('Could not resolve shortTermRentalId for property:', property?.propertyCode || property);
      return; // skip this property — cannot price it without an ID
    }
    try {
      let pictureName = await this.findPropertyById(shortTermRentalId);
      if (pictureName != null) {
        property.thumbImg = "https://thumb.kbmhawaii.com/" + pictureName;
      }
    } catch (err) {
      console.error('Error getting picture name:', err);
    }
    try {
      // Pretax total for the row. In multi-window mode we must hit the
      // rate engine with THIS window's dates; otherwise the row would
      // reflect a price for a different stay. findQuoteTotal currently
      // reads this.quote.checkInDate, so fall through to the variant
      // that accepts explicit dates when we have a per-row window.
      let quoteObj = winStart && winEnd
        ? await this.findQuoteTotalForWindow(shortTermRentalId, winStart, winEnd)
        : await this.findQuoteTotal(shortTermRentalId, null);
      if (quoteObj != null) {
        property.grandTotal        = quoteObj.invoice.grandTotal;
        property.grandTotalWithTax = quoteObj.invoice.grandTotalWithTax;

        // Derive List Price (yieldPrice) from the engine response so it
        // reconciles with the invoice totals the guest actually sees.
        //
        // The raw str_rate.yieldPriceAMT read from property.rates above is
        // only the check-in day's base daily rate — pre-yield, pre-coupon,
        // and unrelated to any invoice line — so it is overridden here.
        //
        // Formula: (pre-coupon nightly × nights − total coupon) ÷ nights
        //   = post-coupon per-night rate (= str_rate_detail_los nightly)
        //
        // Example: ($968.56 × 4 − $774.85) / 4 = $774.85
        //   Scales correctly for any night count and any coupon %.
        //   When no coupon is applied (couponTotal = 0), falls back to
        //   the engine pre-coupon nightly ($968.56).
        const invoiceDetails: any[] = quoteObj.invoice?.invoiceDetails ?? [];
        const nightlyLine  = invoiceDetails.find((d: any) => d.productId === 1);   // "Nightly rate"
        const couponLine   = invoiceDetails.find((d: any) => d.productId === 109);  // "Coupon Discount"
        if (nightlyLine?.finalPrice != null) {
          const numNights   = quoteObj.numberOfNightsStay ?? winNumNights ?? 1;
          const couponTotal = couponLine ? Math.abs(couponLine.finalPrice) : 0;
          yieldPrice = ((nightlyLine.finalPrice * numNights) - couponTotal) / numNights;
          yieldPrice = Math.round(yieldPrice * 100) / 100;   // round to 2 dp
        }
      }
    } catch (err) {
      console.error('Error getting quote total:', err);
    }

    if (isAvailable) {
      this.quoteDetailAPIList.push(
        this.formBuilder.group({
          quoteDetailId: [property?.quoteDetailId],
          createDate: [property?.createDate],
          quoteDetailIdMask: [property?.quoteDetailIdMask],
          quoteStatus: [property?.quoteStatus],
          yieldPrice: [property?.yieldPrice || yieldPrice],
          isAvailable: [isAvailable],
          quotePrice: [this.currencyPipe.transform((property?.yieldPrice || yieldPrice), 'USD', 'symbol', '1.2-2') || ''],
          quoteDiscPct: [property?.quoteDiscPct],
          shortTermRentalId: [shortTermRentalId],
          guestNotes: [property?.guestNotes],
          opsNotes: [],
          addressDevelopmentName: [property.addressDevelopmentName || property?.shortTermRental?.development?.adrDevelopmentName],
          propertyCode: [property.propertyCode || property?.shortTermRental?.propertyCode],
          thumbImg: [property.thumbImg],
          grandTotal: [property.grandTotal],
          grandTotalWithTax: [property.grandTotalWithTax],
          quotePriceUpdated: false,
          // Per-line window (v2.6). Null on legacy quotes; the row inherits
          // the parent form dates in that case. Non-null on multi-window
          // quotes — backend persists these to str_quote_detail.
          checkInDateTime: [winStart || null],
          checkOutDateTime: [winEnd || null],
          numNights: [winNumNights || null]
        })
      );
    } else {
      this.reservedProperties.push(property.propertyCode);
    }
  }

  /**
   * Rate-engine lookup using an explicit window instead of the parent
   * form dates. Multi-window quotes call this so each row shows the
   * pretax/grand-total for its own stay rather than inheriting the
   * envelope span (which would over-count nights).
   */
  private findQuoteTotalForWindow(shortTermRentalId: any, winStart: Date, winEnd: Date): Promise<any> {
    let quoteRequest = {
      checkInDate: moment(winStart).format("YYYY-MM-DD"),
      checkOutDate: moment(winEnd).format("YYYY-MM-DD"),
      numAdults: 2,
      numChildren: 0,
      numPets: '0',
      inquirerIpAddress: '',
      couponCode: '',
      shortTermRentalId: shortTermRentalId,
      quoteNumNight: ''
    };
    return new Promise((resolve, reject) => {
      this.reservationService.getQuote(quoteRequest)
        .subscribe(
          (res: any) => resolve(res.data.quoteSummary.quoteApiList[0]),
          (err: any) => {
            console.error('findQuoteTotalForWindow FAIL:', err);
            this.messageService.add({ severity: 'error', summary: 'Pricing Error', detail: 'Could not load rates for one or more windows. Please try again.', life: 5000 });
            reject(err);
          },
          () => { this.formLoading = false; }
        );
    });
  }

  findQuoteTotal(shortTermRentalId: any, quotedPrice: any): Promise<any> {
    let quoteRequest = {
      checkInDate: moment(this.quote.checkInDate).format("YYYY-MM-DD"),
      checkOutDate: moment(this.quote.checkOutDate).format("YYYY-MM-DD"),
      numAdults: 2,
      numChildren: 0,
      numPets: '0',
      inquirerIpAddress: '',
      couponCode: '',
      shortTermRentalId: shortTermRentalId,
      quoteNumNight: ''
    }
    //Search pretax value using quoted price which can be lesser or higher then los price
    if(quotedPrice != null)
    {
      quoteRequest['quotedPrice'] = quotedPrice;
      return new Promise((resolve, reject) => {
        this.reservationService.getQuoteWithQuotedPrice(quoteRequest)
          .subscribe(
            (res: any) => {
              // Resolve the Promise with the picture file name
              resolve(res.data.quoteSummary.quoteApiList[0]);
            },
            (err: any) => {
              console.error('findQuoteTotal FAIL:', err);
              this.messageService.add({ severity: 'error', summary: 'Pricing Error', detail: 'Could not load rates for one or more properties. Please try again.', life: 5000 });
              reject(err);
            },
            () => {
              this.formLoading = false;
            }
          );
      });
    }
    else
    {
      return new Promise((resolve, reject) => {
        this.reservationService.getQuote(quoteRequest)
          .subscribe(
            (res: any) => {
              // Resolve the Promise with the picture file name
              resolve(res.data.quoteSummary.quoteApiList[0]);
            },
            (err: any) => {
              console.error('findQuoteTotal FAIL:', err);
              this.messageService.add({ severity: 'error', summary: 'Pricing Error', detail: 'Could not load rates for one or more properties. Please try again.', life: 5000 });
              reject(err);
            },
            () => {
              this.formLoading = false;
            }
          );
      });
    }
  }

  findPropertyById(shortTermRentalId: any): Promise<any> {
    return new Promise((resolve) => {
      this.quoteService.findPictureByPropertyId(shortTermRentalId)
        .subscribe(
          (res: any) => {
            // Safely resolve — picture may not exist for every property
            resolve(res?.data?.picture?.fileName ?? null);
          },
          (err: any) => {
            // Don't crash the whole form — just resolve null so the property still loads without a thumbnail
            console.error('Could not load picture for shortTermRentalId=' + shortTermRentalId, err);
            resolve(null);
          },
          () => {
            this.formLoading = false;
          }
        );
    });
  }

  onFormDelete() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete Id# ' + this.quoteId + '?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.formDelete()
      },
      reject: () => {
        this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
      }
    });
  }


  formUpdate() {
    try {
      // stop here if form is invalid
      this.form1.markAllAsTouched();
      if (this.form1.invalid) {
        return;
      }
      if (this.quoteDetailAPIList.length === 0) {
        this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please select at least one property before updating the quote.', life: 5000 });
        return;
      }
      this.formLoading = true;
      let obj = this.form1.value;
      obj.msgOrigin = this.msgOrigin;
      obj.msgRequest = this.msgRequest;
      obj.quoteDetailAPIList = obj.quoteDetailAPIList.map((quote) => ({
        ...quote,
        quotePrice: typeof quote.quotePrice === 'string' ? parseFloat(quote.quotePrice.replace(/[$,]/g, '')) : quote.quotePrice
      }));
      obj.quoteId = this.quoteId;
      this.quoteService.update(obj)
        .subscribe(
          (res: any) => {
            console.log('property-update SUCCESS: ' + JSON.stringify(res));
            this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
            this.quote = res.data.quote;
            this.doUpdate.emit(this.quote);
            this.doEmailEvent.emit(this.quote);
          },
          (err: any) => {
            // Do NOT reset formLoading on error — a 504 means the backend may
            // have already saved the quote update. Keep button disabled to block re-submit.
            console.log('property-update FAIL: ' + JSON.stringify(err));
            this.alertService.error(err);
          },
          () => { this.formLoading = false; });
    } catch (error) {
      this.formLoading = false;
      console.log('Form Invalid: ' + JSON.stringify(error));
      this.alertService.error(error);
    }
  }

  formDelete() {
    try {
      this.quoteService.delete(this.quote.quoteId)
        .subscribe(
          (res: any) => {
            console.log('quote-delete SUCCESS: ' + JSON.stringify(res));
            this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
            this.doDelete.emit(this.quote);
          },
          (err: any) => {
            console.log('quote-add FAIL: ' + JSON.stringify(err));
            this.alertService.error(err);
          },
          () => { this.formLoading = false; });
    } catch (error) {
      this.alertService.error(error);
    }
  }

  formBuild() {
    this.form1 = this.formBuilder.group({
      shortTermRentalId: [],
      numAdults: [2, Validators.required],
      numChildren: [0],
      checkOutDateTime: ['', Validators.required],
      quoteExpirationDateTime: ['', Validators.required],
      checkInDateTime: ['', Validators.required],
      guestNotes: [],
      opsNotes: [],
      sendEmail: [true],
      sendText: [],
      startFollowUpDrip: [true],
      createDate: [],
      lastUpdate: [],
      quoteIdMask: [],
      guestMasterProfileId: [],
      reservationistUserId: [],
      guestProfile: this.formBuilder.group({
        guestMasterProfileId: [''],
        firstName: [],
        lastName: [],
        emailAddress: ['', Validators.required],
        guestCompanions: new UntypedFormArray([]),
        mobilePhone: [''],
      }),
      quoteDetailAPIList: this.formBuilder.array([]
      ),
    });
  }

  get quoteDetailAPIList(): FormArray {
    return this.form1.get('quoteDetailAPIList') as FormArray;
  }

  // change checkout date
  checkOutChange(e: any) {
    this.isDisplayClearDates = true;
    // this.guestDropdownCount.show();
  }

  // if select checkout date at that time check checkin date must be below lessthan one day of selected date
  onKeyDateForCheckOutDate(event: any) {
    event.target.value = event.target.value.replace(/^(\d{2})(\d{2})(\d{4})/, '$1-$2-$3');
    if (this.form1.value.checkInDateTime) {
      let today = new Date(this.form1.value.checkInDateTime);
      this.updatedCheckInDate = new Date(today.setDate(today.getDate() + 1));
      this.checkOutDateBasedOnCheckinDate = this.updatedCheckInDate;
    }
  }

  // compare checkin and checkout dates
  compareDate(date: any) {
    this.isDateLessThanCheckinDate = false;
    if (Date.parse(date.target.value) < Date.parse(moment(this.checkOutDateBasedOnCheckinDate).format("MM/DD/YYYY"))) {
      this.isDateLessThanCheckinDate = true;
    }
  }

  // open guest modal
  openGuestModal(e: any) {
    // this.dropdown.show();
  }

  // change checking date — also clears checkout so the updated minDate is enforced on the new pick
  checkInChange(e: any) {
    let today = new Date(e.valueOf());
    this.updatedCheckInDate = new Date(today.setDate(today.getDate() + 1));
    this.checkOutDateBasedOnCheckinDate = this.updatedCheckInDate;
    this.isDisplayClearDates = true;
    this.form1.patchValue({ checkOutDateTime: null });
    window.setTimeout(() => {
      this.checkOutDateCalender.inputfieldViewChild.nativeElement.focus();
    }, 1);
  }

  removeProperty(index)
  {
    const propertyCode = this.quoteDetailAPIList.at(index)?.get('propertyCode')?.value || 'this property';
    this.confirmationService.confirm({
      message: `Remove ${propertyCode} from this quote?`,
      header: 'Confirm Remove',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.selectedProperties.splice(index, 1);
        this.quoteDetailAPIList.removeAt(index);
        this.doUpdate.emit(this.selectedProperties);
      },
      reject: () => {}
    });
  }

  async updateQuotePrice(i: number) {
    const quoteDetail = this.quoteDetailAPIList.at(i);
    const yieldPrice = quoteDetail.get('yieldPrice')?.value;
    const quoteDiscPct = quoteDetail.get('quoteDiscPct')?.value;

    if (yieldPrice != null && quoteDiscPct != null) {
      // quoteDiscPct values are stored as positive decimals (e.g. 0.10 = 10% off).
      // Math.abs() guards against any accidental negative value being passed in.
      let newQuotePrice = yieldPrice * (1 - Math.abs(quoteDiscPct));
      newQuotePrice = Math.round(newQuotePrice * 100) / 100;
      quoteDetail.get('quotePrice')?.setValue(newQuotePrice);
      quoteDetail.get('quotePriceUpdated')?.setValue(true);
      let quoteObj = await this.findQuoteTotal(quoteDetail.get('shortTermRentalId')?.value, newQuotePrice);
      if (quoteObj != null) {
        quoteDetail.get('grandTotal')?.setValue(quoteObj.invoice.grandTotal);
        quoteDetail.get('grandTotalWithTax')?.setValue(quoteObj.invoice.grandTotalWithTax);
      }
    }
  }

  // if select checkin date at that time checkout date set as null
  onKeyDate(event: any) {
    event.target.value = event.target.value.replace(/^(\d{2})(\d{2})(\d{4})/, '$1-$2-$3');
    // this.form1.patchValue({
    // 	checkOutDate: ""
    // });
  }

  sendQuote()
  {
    //console.log(this.form1.value);
    try {
      // stop here if form is invalid
      this.form1.markAllAsTouched();
      // Check if either sendEmail or sendText is selected
      const sendEmail = this.form1.get('sendEmail')?.value;
      const sendText = this.form1.get('sendText')?.value;
      if (!sendEmail && !sendText) {
        this.form1.get('sendEmail')?.setErrors({ required: true });
      } else {
        // If either checkbox is selected, remove the validation error
        this.form1.get('sendEmail')?.setErrors(null);
        this.form1.get('sendText')?.setErrors(null);
      }
      if (this.form1.invalid) {
        return;
      }
      if (this.quoteDetailAPIList.length === 0) {
        this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please select at least one property before sending the quote.', life: 5000 });
        return;
      }
      // Backend (QuoteBL.createQuote) calls guestMasterProfileService.findById
      // on the incoming guestMasterProfileId and dereferences the resulting
      // VO; a null id guarantees a 400/NPE. Block here with a clear message
      // so the rep doesn't hit the scary server error. The new-guest banner
      // already exposes "Create Profile" / "Find Existing" buttons that
      // resolve this in one click.
      if (!this.form1.value.guestMasterProfileId) {
        this.needsNewGmp = this.needsNewGmp || !!this.incomingGuestInfo?.guestEmail;
        this.messageService.add({
          severity: 'warn',
          summary: 'Guest profile required',
          detail: 'Please create or select a Guest Master Profile before sending the quote.',
          life: 5000
        });
        return;
      }
      this.formLoading = true;
      let obj = this.form1.value;
      if(obj.quoteDetailAPIList)
      {
        obj.quoteDetailAPIList = obj.quoteDetailAPIList.map((quote) => ({
          ...quote,
          quotePrice: typeof quote.quotePrice === 'string' ? parseFloat(quote.quotePrice.replace(/[$,]/g, '')) : quote.quotePrice,
          // Per-row window dates (v2.6). Apply the same timezone shift we
          // apply to the parent dates so server-side LocalDate parsing
          // doesn't slip by a day.  Null-safe: legacy single-window rows
          // leave these null and the backend inherits from the parent.
          checkInDateTime: quote.checkInDateTime ? this.addTimeOffsetToLocalDateTime(new Date(quote.checkInDateTime)) : null,
          checkOutDateTime: quote.checkOutDateTime ? this.addTimeOffsetToLocalDateTime(new Date(quote.checkOutDateTime)) : null
        }));
      }
      if (this.isMultiWindow) {
        // Multi-window quote: the parent QuoteVO must NOT carry a single
        // checkIn/checkOut — each row has its own. Backend QuoteBL.createQuote
        // stamps the envelope span [min(lineCheckIn), max(lineCheckOut)]
        // on the parent once the details are saved, so reports and list
        // views still show a sensible date range.
        obj.checkInDateTime = null;
        obj.checkOutDateTime = null;
        obj.numNights = null;
      } else {
        if (obj.checkInDateTime) {
          obj.checkInDateTime = this.addTimeOffsetToLocalDateTime(obj.checkInDateTime);
        }
        if (obj.checkOutDateTime) {
          obj.checkOutDateTime = this.addTimeOffsetToLocalDateTime(obj.checkOutDateTime);
        }
      }
      obj.msgOrigin = this.msgOrigin;
      obj.msgRequest = this.msgRequest;
      // Persist the inbox-message → quote link explicitly. Backend will
      // also infer this from msgRequest.replyToMsgId as a fallback, but
      // setting it on the wire makes the relationship visible in network
      // logs and protects against future refactors that drop msgRequest
      // from the save payload (e.g. direct-send quote flows).
      obj.sourceMsgId = this.msgRequest?.replyToMsgId ?? null;
      // Source carried in from the dialog opener (best-fit / quote-list /
      // tape-chart / inbox). See QuoteSource enum; null → backend OTHER.
      obj.source = this.source;
      this.quoteService.add(obj)
        .subscribe(
          (res: any) => {
            console.log('quote-add SUCCESS: ' + JSON.stringify(res));
            const timeZone = 'America/New_York'; // Example time zone

            const quoteExpirationDateTimeStr = new Intl.DateTimeFormat('en-US', {
              timeZone,
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            }).format(new Date(res?.data?.quote?.createDate));
            const message = 'Quote #' + res?.data?.quote?.quoteId + ' saved — email is being sent to '
              + res?.data?.quote?.guestMasterProfileAPI?.emailAddress
              + '. You will be notified if delivery fails.';
            // Do NOT show the toast here — the dialog is closed by the parent immediately
            // after doAdd fires, destroying this component's scoped MessageService and
            // p-toast before the notification can be seen.  The parent (quote-list) shows
            // the toast at its own level after the dialog is closed.
            // Emit both the message and the quoteId so the parent can poll for email
            // delivery status after the async SendGrid call completes (~3-5 seconds).
            this.doAdd.emit({ message, quoteId: res?.data?.quote?.quoteId });
          },
          (err: any) => {
            // Do NOT reset formLoading on error — a 504 means the backend may
            // have already saved the quote. Keep button disabled to block re-submit.
            console.log('quote-add FAIL: ' + JSON.stringify(err));
            this.alertService.error(err);
          },
          () => { this.formLoading = false; });

    } catch (error) {
      this.formLoading = false;         // unblock if validation throws
      this.alertService.error(error);
    }
  }

  openQuoteWindow(i: number, propertyCode)
  {
    const quoteDetail = this.quoteDetailAPIList.at(i);
    this.quotePropertyCode = propertyCode;
    this.formError = null;
    // For multi-window quotes each row carries its own checkInDateTime /
    // checkOutDateTime (set by buildQuoteDetailRow). Use those when present
    // so the invoice preview matches the individual window, not the envelope
    // span of all selected windows (which would show the wrong dates and
    // store the wrong check-in/out when a reservation is later created).
    const rowCheckIn  = quoteDetail.get('checkInDateTime')?.value  || this.quote.checkInDate;
    const rowCheckOut = quoteDetail.get('checkOutDateTime')?.value || this.quote.checkOutDate;
    this.quoteRequest = {
      checkInDate:  moment(rowCheckIn).format("YYYY-MM-DD"),
      checkOutDate: moment(rowCheckOut).format("YYYY-MM-DD"),
      numAdults: 2,
      numChildren: 0,
      numPets: '0',
      inquirerIpAddress: '',
      couponCode: '',
      shortTermRentalId: quoteDetail.get('shortTermRentalId')?.value,
      quoteNumNight: '',
      quotePriceUpdated: quoteDetail.get('quotePriceUpdated')?.value,
    }
    if(quoteDetail.get('quotePriceUpdated')?.value != null && quoteDetail.get('quotePriceUpdated')?.value == true)
    {
      this.quoteRequest['quotedPrice'] = quoteDetail.get('quotePrice')?.value
    }
    // Clear the previous quote immediately so the dialog opens without
    // stale data from the last property the rep inspected, then flip the
    // loading flag so the template renders a spinner in place of the
    // invoice block until getQuoteForSearchRates() resolves.
    this.wikiQuote = null;
    this.formError = null;
    this.loadingWikiQuote = true;
    this.showQuoteWindow = true;
    this.getQuoteForSearchRates(this.quoteRequest)
  }

  getQuoteForSearchRates(request: any) {
    try {
      this.formError = null;
      if(request.quotePriceUpdated != null && request.quotePriceUpdated)
      {
        this.reservationService.getQuoteWithQuotedPrice(request)
          .subscribe(
            res => {
              console.log('getQuote SUCCESS: ' + JSON.stringify(res));
              this.wikiQuote = res.data.quoteSummary.quoteApiList[0];
              sessionStorage.removeItem('quote');
              sessionStorage.setItem('quote', JSON.stringify(this.wikiQuote));
              if(this.checkCouponValidOrNot && !this.wikiQuote?.couponApplied){
                this.checkCouponValidOrNot = false;
                this.invalidCouponCode = true;
              }
              if (res.data.quoteSummary.quoteApiList[0].errors) {
                this.formError = res.data.quoteSummary.quoteApiList[0].errors[res.data.quoteSummary.quoteApiList[0].errors.length - 1];
              }
              this.loadingWikiQuote = false;
            },
            err => {
              console.log('getQuote FAIL: ' + JSON.stringify(err));
              this.formError = err.error;
              this.loadingWikiQuote = false;
            },
            () => {
            });
      }
      else
      {
        this.reservationService.getQuote(request)
          .subscribe(
            res => {
              console.log('getQuote SUCCESS: ' + JSON.stringify(res));
              this.wikiQuote = res.data.quoteSummary.quoteApiList[0];
              sessionStorage.removeItem('quote');
              sessionStorage.setItem('quote', JSON.stringify(this.wikiQuote));
              if(this.checkCouponValidOrNot && !this.wikiQuote?.couponApplied){
                this.checkCouponValidOrNot = false;
                this.invalidCouponCode = true;
              }
              if (res.data.quoteSummary.quoteApiList[0].errors) {
                this.formError = res.data.quoteSummary.quoteApiList[0].errors[res.data.quoteSummary.quoteApiList[0].errors.length - 1];
              }
              this.loadingWikiQuote = false;
            },
            err => {
              console.log('getQuote FAIL: ' + JSON.stringify(err));
              this.formError = err.error;
              this.loadingWikiQuote = false;
            },
            () => {
            });
      }

    } catch (error) {
      // Defensive: if the subscribe() call itself throws (config error,
      // not an API error), we'd otherwise be stuck with the spinner on.
      this.loadingWikiQuote = false;
    }
  }

  showCouponDialog(){
    this.couponCode = "";
    this.isSearching = false;
    this.invalidCouponCode = false;
    this.couponCodeSuccessMsg = false;
    this.isCouponCodeIsApply = false;
    this.checkCouponValidOrNot = false;
    this.showCoupon = true;
  }

  applyCouponCode(){
    this.isSearching = false;
    this.invalidCouponCode = false;
    this.couponCodeSuccessMsg = false;
    this.isCouponCodeIsApply = false;
    this.checkCouponValidOrNot = false;
    if(this.couponCode === "" || this.couponCode === null || this.couponCode === undefined){
      this.isCouponCodeIsApply = true;
    }else{
      this.checkCouponValidOrNot = true;
      this.quoteRequest['couponCode'] = this.couponCode;
      this.getQuoteForSearchRates(this.quoteRequest);
    }
  }

  findOrEditGuest(){
    this.receivedGuestMasterProfileId = this.receivedGuestMasterProfileId || this.quote.guestMasterProfileId;
    if (this.receivedGuestMasterProfileId==null){
      this.findGuest();
    }else{
     this.EditGuest();
    }
  }
  findGuest()
  {

    const dialogConfig = new MatDialogConfig();
    const data = {
    };
    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.maxWidth = "120vw";
    dialogConfig.width = "60vw";
    dialogConfig.data = {
      title : "Quote",
      showCloseButton: true,
      quote: data
    };
    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(GuestMasterProfileListComponent, dialogConfig);
    dialogRef.componentInstance.doUpdate.subscribe(
      data => {
        console.log('Dialog output:', data);
        if(data.guestMasterProfileId)
        {
          this.receivedGuestMasterProfileId=data.guestMasterProfileId;
          // Rep resolved the guest via the existing list — we are no
          // longer in "new guest" limbo regardless of which route they
          // took (find existing vs. create new from the list dialog).
          this.clearNeedsNewGmp();
          this.form1.patchValue({
            guestMasterProfileId: data.guestMasterProfileId,
            guestProfile: {
              guestMasterProfileId: data.guestMasterProfileId
            }
          });
        }
        if(data.firstName)
        {
          this.form1.patchValue({
            guestProfile: {
              firstName: data.firstName
            }
          });
        }
        if(data.lastName)
        {
          this.form1.patchValue({
            guestProfile: {
              lastName: data.lastName
            }
          });
        }
        if(data.emailAddress)
        {
          this.form1.patchValue({
            guestProfile: {
              emailAddress: data.emailAddress
            }
          });
        }
        if(data.mobilePhone)
        {
          this.form1.patchValue({
            guestProfile: {
              mobilePhone: data.mobilePhone
            }
          });
        }
        // close the dialogBox
        dialogRef.close();
      });

  }

  EditGuest()
  {
    this.guestMasterProfileService.findById(this.receivedGuestMasterProfileId).subscribe(res => {
      const dialogConfig = new MatDialogConfig();
      const data = {
      };
      // settings
      dialogConfig.disableClose = false;
      dialogConfig.autoFocus = true;
      dialogConfig.hasBackdrop = false;
      //dialogConfig.panelClass = "edit-custom-modalbox";
      // pass data, ie rateBuicket: this.shortTermRentalId,
      dialogConfig.data = {
        guestMasterProfile: res?.data?.guestMasterProfile,
        title: 'Edit Guest Master Profile'
      };
      // open the popup and get a reference to it called dialogRef
      const dialogRef = this.dialog.open(GuestMasterProfileComponent, dialogConfig);
      dialogRef.componentInstance.doUpdatePreSelected.subscribe(
        data => {
          console.log("received data from dialog-component");
          console.log(data);
          if (data.firstName){
            this.form1.patchValue({
              guestProfile: {
                firstName:data.firstName
              }
            });
          }
          if (data.lastName){
            this.form1.patchValue({
              guestProfile: {
                lastName: data.lastName
              }
            });
          }
          if (data.emailAddress){
            this.form1.patchValue({
              guestProfile: {
                emailAddress: data.emailAddress
              }
            });
          }
          if (data.emailAddress){
            this.form1.patchValue({
              guestProfile: {
                mobilePhone: data.mobilePhone
              }
            });
          }
          dialogRef.close();
        });
    });


  }

  /**
	 * new Date('String Date') creates date with timezone offset hence this method add timezone hours
	 * @param datetime
	 * @returns
	 */
	addTimeOffsetToLocalDateTime(datetime) {
		let offsetMin = datetime.getTimezoneOffset();
		// If positive means timezone is in minus of UTC
		if(offsetMin > 0)
		{
			return new Date(datetime.getTime() + (datetime.getTimezoneOffset() * 60000));
		}
		else
		{
			return new Date(datetime.getTime() - (datetime.getTimezoneOffset() * 60000));
		}
	}

  /** Load/refresh the drip status panel for the current quote. */
  loadDripInstance(): void {
    if (!this.quoteId) { return; }
    this.dripLoading = true;
    this.dripCampaignService.findInstanceForEntity(4, this.quoteId).subscribe({
      next: (instance) => {
        this.dripInstance = instance;
        this.dripLoading = false;
      },
      error: () => { this.dripLoading = false; }
    });
  }

  pauseDrip(): void {
    if (!this.dripInstance) { return; }
    this.dripCampaignService.pauseInstance(this.dripInstance.dripCampaignInstanceId).subscribe({
      next: () => {
        this.messageService.add({ severity: 'info', summary: 'Drip paused', detail: 'Follow-up campaign has been paused.', life: 3000 });
        this.loadDripInstance();
      }
    });
  }

  resumeDrip(): void {
    if (!this.dripInstance) { return; }
    this.dripCampaignService.resumeInstance(this.dripInstance.dripCampaignInstanceId).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Drip resumed', detail: 'Follow-up campaign has been resumed.', life: 3000 });
        this.loadDripInstance();
      }
    });
  }

  cancelDrip(): void {
    if (!this.dripInstance) { return; }
    this.confirmationService.confirm({
      message: 'Cancel the automated follow-up drip campaign for this quote? This cannot be undone.',
      header: 'Cancel Drip Campaign',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.dripCampaignService.cancelInstance(this.dripInstance.dripCampaignInstanceId, 'Manually cancelled by admin').subscribe({
          next: () => {
            this.messageService.add({ severity: 'warn', summary: 'Drip cancelled', detail: 'Follow-up campaign has been cancelled.', life: 3000 });
            this.loadDripInstance();
          }
        });
      }
    });
  }

  closePopUpOfQuote(){
    // Previously only emitted doUpdate — parent (tape chart) subscribed but
    // didn't call dialogRef.close(). Now we close the dialog directly. We
    // still emit so any parent listeners are notified.
    this.doUpdate.emit("");
    if (this.dialogRef) { this.dialogRef.close(); }
  }

  ngOnDestroy(): void {
    this.currentUserSubscription?.unsubscribe();
  }

  findById(){
      this.quoteService.findById(this.quoteId)
        .subscribe(
          (res: any) => {
            // Resolve the Promise with the picture file name
            this.quote = this.mapSourceToTarget(res?.data?.quote);
            this.selectedProperties = res?.data?.quote?.quoteDetailAPIList;
    console.log(this.selectedProperties)
            this.quoteDataLoad();
          },
          (err: any) => {
            console.log('quote-add FAIL: ' + JSON.stringify(err));
            // Reject the Promise if there's an error
          },
          () => {
            this.formLoading = false;
          }
        );
  }

  mapSourceToTarget(source: any): any {
    return {
      quoteId: source.quoteId,
	  createDate: source.createDate,
	  lastUpdate: source.lastUpdate,
	  quoteIdMask: source.quoteIdMask,
      shortTermRentalId: source.shortTermRentalId,
      numAdults: source.numAdults,
      numChildren: source.numChildren,
      numQuoteStatus: source.quoteDetailAPIList && source.quoteDetailAPIList[0]?.quoteStatus, // Assuming a mapping logic
      checkOutDate: source.checkOutDateTime || "",
      quoteExpirationDateTime: source.quoteExpirationDateTime || "",
      checkInDate: source.checkInDateTime || "",
      guestNotes: source.guestNotes,
      opsNotes: source.opsNotes,
      sendEmail: source.sendEmail,
      sendText: source.sendText,
      guestMasterProfileId: source.guestMasterProfileId,
      reservationistUserId: source.reservationistUserId,
      guestProfile: {
        guestMasterProfileId: source.guestMasterProfileAPI.guestMasterProfileId?.toString() || "",
        firstName: source.guestMasterProfileAPI.firstName || null,
        lastName: source.guestMasterProfileAPI.lastName || null,
        emailAddress: source.guestMasterProfileAPI.emailAddress || "",
        guestCompanions: [],
        mobilePhone: source.guestMasterProfileAPI.mobilePhone || "",
      },
      selectedProperties: source.quoteDetailAPIList,
    };
  }
}
